/**
 * Item page — V98-3-5. Route: /w/:slug/app/items/:kind/:id?from=…
 * (PRD G-3 "the item page shows the actions for each hat separately";
 * research §2 item 4; ADR-0046 Appendix A).
 *
 * Program-neutral: one page per item whatever program(s) it counts in, with
 * a return crumb from `from` (today | program:<key>). The viewer's hats on
 * the item come from GET /today; each hat gets its own block with only the
 * actions that hat may take, and SoD machine states render as states
 * ("Routed · with Grace · you prepared this"), never as disabled buttons.
 *
 *   signature_request   approver: SignatureCard sign / send back + the
 *                       locked content (#locked, "Read vN")
 *                       author / requester: routing state, the alternatives
 *                       in rule-18 order + the compensating review, or —
 *                       when sent back — revise + resubmit (v+1)
 *   access_review       per-row routing + reciprocal handling
 *   independent_check   checker: conclusion + substantive items + note, with
 *                       what it is about; escalation owner: names the
 *                       checker; acknowledge, nudge, reassign the checker
 *                       (eligible picker + reason) or extend (≤ 30 days)
 *   coverage_ask        confirm (with the consequence preview) / delegate /
 *                       sign remotely / decline (reason)
 *   work_item           its server action contract (done here, evidence,
 *                       the owning record, revise)
 *   finding · obligation_occurrence   deep link to the record; with no hat on
 *                       it, the page sends any member to the record itself
 *                       (/findings?finding=<id>) — never "Nothing for you to
 *                       do here" from a queue row (V98-5 review T5-H14)
 *   obsolete signature  "vN is obsolete · why", the newest version (or who
 *                       freezes the next one), the old content behind a
 *                       disclosure (T5-H10)
 *   scn                 V98-5-3: change notice — suggested vs decided class,
 *                       co-approval, advisory assessor view, the boundary-
 *                       checked draft and the confirm-to-send (ScnItem)
 *   no hat (lead/admin) read-only status: who has it + nudge / reassign
 *
 * V98-3 review U1 (no dead ends), U9–U11, U14. The server enforces every
 * actor rule; this page only shows them, and hides what this viewer's role
 * cannot do.
 */
import { useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import type { OwedRow, WaitingRow } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { getToday } from '../lib/api';
import { getScn } from '../lib/cert-ops-api';
import { absencePreview, answerAsk, getSignature, listBlockers, listMyAsks, signRemote, type CoverageItemKind, type SignatureView } from '../lib/coverage-api';
import { queryKeys } from '../lib/query-keys';
import { PROGRAM_LABELS, isProgramKey } from '../lib/nav-config';
import { usePrograms } from '../lib/workspace-navigation';
import { HAT_TAG, day, dueChip, firstName, fmtDay, hatTag, nameOf, type Names } from '../lib/today-model';
import { SignatureAction } from '../components/today/SignQueue';
import { AdvisorDialog } from '../components/today/SoloCard';
import { AbsencePreviewList } from '../components/today/CoverageCards';
import { ActionDialog, Chip, errorText, useAnnounce, useFocusAfter } from '../components/today/shared';
import { SodPanel } from '../components/item/SodPanel';
import { CheckerForm, EscalationFollowUp } from '../components/item/CheckerForm';
import { AccessReviewTask } from '../components/item/AccessReviewTask';
import { AccessReviewProgressPanel } from '../components/item/AccessReviewProgress';
import { LockedContent, NobodyEligible, RecordBlock, ReviseBlock, StatusBlock, WorkItemBlock } from '../components/item/ItemBlocks';
import { LeadActions } from '../components/item/LeadActions';
import { ScnItem } from '../components/item/ScnItem';
import { useNames } from './Today';
import './today.css';
import './cert-ops.css';

const KIND_LABEL: Record<string, string> = {
  signature_request: 'Signature', access_review: 'Access review', independent_check: 'Independent check', coverage_ask: 'Ask',
  finding: 'Finding', obligation_occurrence: 'Recurring obligation', work_item: 'Work item', scn: 'Change notice',
};

export function ItemPage() {
  const { kind = '', id = '' } = useParams<{ kind: string; id: string }>();
  const [sp] = useSearchParams();
  const { workspace, role } = useWorkspace();
  const canManage = role === 'owner' || role === 'admin';
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 30_000 });
  const names = useNames(slug);
  const focusLater = useFocusAfter();
  const [announceNode, announce] = useAnnounce();
  const onDone = (msg: string, focusId: string) => { focusLater(focusId); announce(msg); };
  const [advisor, setAdvisor] = useState(false);
  // T5-H12: the progress panel's "N to review" filters the rows below to one system.
  const [arSystem, setArSystem] = useState<string | null>(null);
  const advisorOpener = useRef<string>('item-h1');
  const openAdvisor = canManage ? (el: HTMLElement) => { advisorOpener.current = el.id || 'item-h1'; setAdvisor(true); } : undefined;

  const from = sp.get('from') ?? 'today';
  const program = from.startsWith('program:') ? from.slice('program:'.length) : null;
  const crumb = program && isProgramKey(program)
    ? { to: `${prefix}/programs/${program}`, label: PROGRAM_LABELS[program] ?? 'Program' }
    : { to: `${prefix}/today`, label: 'Today' };
  const batchIds = (sp.get('batch') ?? '').split(',').filter(Boolean);

  const view = today.data;
  const me = view?.viewer.user_id ?? '';
  const isLead = !!view && (view.viewer.is_admin || view.viewer.lead_program_ids.length > 0);
  const row: OwedRow | undefined = view?.owed.find((r) => r.ref.kind === kind && r.ref.id === id);
  const waiting: WaitingRow | undefined = view?.waiting.find((r) => r.ref.kind === kind && r.ref.id === id);
  const sig = useQuery({ queryKey: ['signature', slug, id], queryFn: () => getSignature(slug, id), enabled: kind === 'signature_request', retry: false });
  // V98-5-3: a change notice reads its own record (any member); the title comes from it.
  const scn = useQuery({ queryKey: ['scn', slug, id], queryFn: () => getScn(slug, id), enabled: kind === 'scn', retry: false });
  // A lead or admin with no hat on the item gets a read-only status (U1) from the caller-scoped blocker list.
  const needStatus = !!view && !row && !waiting && isLead && kind !== 'signature_request' && kind !== 'scn';
  const blockers = useQuery({ queryKey: ['blockers', slug], queryFn: () => listBlockers(slug), enabled: needStatus, staleTime: 30_000, retry: false });
  const statusRow = blockers.data?.find((b) => b.ref?.kind === kind && b.ref.id === id) ?? null;
  // A finding is fixed in its program's Operate queue: the program it was opened from, else the first program it counts in.
  const programs = usePrograms(kind === 'finding' ? slug : undefined);
  const findingProgram = useMemo(() => {
    if (kind !== 'finding') return null;
    if (program && isProgramKey(program)) return program;
    const ids = row?.program_ids ?? statusRow?.programs ?? [];
    const cards = programs.data?.programs ?? [];
    return ids.map((pid) => cards.find((c) => c.profile_id === pid)?.program).find((k) => !!k && isProgramKey(k)) ?? null;
  }, [kind, program, row, statusRow, programs.data]);
  const title = row?.title ?? waiting?.title ?? sig.data?.request.title ?? scn.data?.change.title ?? statusRow?.title ?? `${KIND_LABEL[kind] ?? 'Item'} ${id}`;
  const todayDay = view ? day(view.generated_at) : new Date().toISOString().slice(0, 10);

  if (today.isLoading || (kind === 'signature_request' && sig.isLoading) || (needStatus && blockers.isLoading)) return <div className="gp-item"><Spinner label="Loading item" /></div>;

  const hats = row ? row.hats : [];
  const hatLine = row ? [...new Set(row.hats.map((h) => (h === row.hat ? hatTag(row, names) : HAT_TAG[h] ?? h)))].join(' · ') : null;
  const chip = row ? dueChip(row, todayDay) : waiting ? dueChip(waiting, todayDay) : statusRow ? dueChip(statusRow, todayDay) : null;

  // D-106-18 / G2: pending result reviews are taken on the program's Prepare review panel.
  if (kind === 'result_review' && row?.act?.href) return <Navigate replace to={`${prefix}${row.act.href}`} />;

  // T5-H14: a finding / recurring occurrence with no hat for this viewer opens its own record (any member can read it).
  if ((kind === 'finding' || kind === 'obligation_occurrence') && !row && !waiting && !statusRow) {
    return <Navigate replace to={kind === 'finding' ? `${prefix}/findings?finding=${encodeURIComponent(id)}` : `${prefix}/deliverables?occurrence=${encodeURIComponent(id)}`} />;
  }

  let body: React.ReactNode;
  if (kind === 'scn') {
    body = scn.error ? <NotOpen prefix={prefix} /> : <ScnItem slug={slug} prefix={prefix} id={id} me={me} names={names} onDone={onDone} />;
  } else if (kind === 'signature_request') {
    body = sig.error || !sig.data ? <NotOpen prefix={prefix} />
      : <SignatureBlocks slug={slug} prefix={prefix} me={me} row={row} names={names} todayDay={todayDay} batchIds={batchIds} isLead={isLead}
          request={sig.data.request} pkg={sig.data.package ?? null} onDone={onDone} onAdvisor={openAdvisor} />;
  } else if (kind === 'access_review') {
    // V98-5-2: the task page — per-system progress, attest, the response when late — above the per-row task.
    const progress = <AccessReviewProgressPanel slug={slug} prefix={prefix} reviewId={id} me={me} names={names} onDone={onDone}
      onFilter={row || waiting ? (s) => { setArSystem(s); focusLater('rows-filter'); } : undefined} />;
    body = row || waiting ? <>{progress}
      <HatBlock title={row ? (hats.includes('access_reviewer') && hats.length > 1 ? 'Your rows · reviewer and approver' : hats.includes('access_reviewer') ? 'Your rows · reviewer' : 'Your rows · approver') : 'Rows'}>
        <AccessReviewTask slug={slug} reviewId={id} me={me} names={names} isReviewer={hats.includes('access_reviewer')}
          system={arSystem} onClearSystem={() => { setArSystem(null); focusLater('review-state'); }}
          onAddParty={openAdvisor ?? (() => undefined)} onDone={onDone} />
      </HatBlock></>
      : statusRow ? <>{progress}<StatusBlock slug={slug} prefix={prefix} row={statusRow} names={names} canAssign={statusRow.can_assign ?? canManage} onDone={onDone} todayDay={todayDay} /></> : <NotOpen prefix={prefix} />;
  } else if (kind === 'independent_check') {
    body = row?.action === 'conclude_check' ? <HatBlock title="As checker"><CheckerForm slug={slug} prefix={prefix} checkId={id} about={row.about} onDone={onDone} /></HatBlock>
      : row?.action === 'handle_escalation' ? (
        <HatBlock title="Escalated to you">
          <EscalationFollowUp slug={slug} prefix={prefix} checkId={id} itemKey={row.key} title={title} about={row.about}
            escalation={row.escalation} names={names} todayDay={todayDay} canNudge={isLead} onAddParty={openAdvisor} onDone={onDone} />
        </HatBlock>
      )
      : waiting ? <HatBlock title="Your approval"><div className="gp-state"><strong>{`Check by ${nameOf(names, waiting.waiting_on_user_id)}`}</strong><span className="gp-dim">{`Only ${firstName(nameOf(names, waiting.waiting_on_user_id))} closes it${waiting.due_at ? ` · by ${fmtDay(waiting.due_at)}` : ''}.`}</span></div></HatBlock>
      : statusRow ? <StatusBlock slug={slug} prefix={prefix} row={statusRow} names={names} canAssign={statusRow.can_assign ?? canManage} onDone={onDone} todayDay={todayDay} />
      : <NotOpen prefix={prefix} />;
  } else if (kind === 'coverage_ask') {
    body = row ? <AskBlock slug={slug} prefix={prefix} askId={id} names={names} me={me} todayDay={todayDay} onDone={onDone} /> : <NotOpen prefix={prefix} />;
  } else if (row) {
    body = (
      <HatBlock title={row.on_behalf_of_user_id ? `For ${firstName(nameOf(names, row.on_behalf_of_user_id))}` : HAT_TAG[row.hat] ? `As ${HAT_TAG[row.hat]!.toLowerCase()}` : 'Yours'}>
        {row.blocked ? <NobodyEligible slug={slug} title={title} onAddParty={openAdvisor} />
          : kind === 'work_item' ? <WorkItemBlock slug={slug} prefix={prefix} row={row} names={names} onDone={onDone} />
          : <RecordBlock kind={kind} id={id} prefix={prefix} program={findingProgram} />}
      </HatBlock>
    );
  } else if (waiting) {
    body = <HatBlock title="Status"><div className="gp-state"><strong>{`With ${nameOf(names, waiting.waiting_on_user_id, 'nobody yet')}`}</strong></div></HatBlock>;
  } else if (statusRow) {
    body = <StatusBlock slug={slug} prefix={prefix} row={statusRow} names={names} canAssign={statusRow.can_assign ?? canManage} onDone={onDone} todayDay={todayDay} program={findingProgram} />;
  } else {
    body = <NotOpen prefix={prefix} />;
  }

  return (
    <div className="gp-item">
      {announceNode}
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        <Link to={crumb.to}>{`‹ ${crumb.label}`}</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{KIND_LABEL[kind] ?? 'Item'}</span>
      </nav>
      <PageHeader variant="app" title={title} headingId="item-h1"
        subtitle={chip || hatLine || waiting ? (
          <span className="gp-ow-meta">
            {chip ? <Chip text={chip.text} tone={chip.tone} /> : null}
            {hatLine ? <span>{`Your part: ${hatLine}`}</span> : waiting ? <span>{`Waiting on ${nameOf(names, waiting.waiting_on_user_id, 'nobody yet')}`}</span> : null}
          </span>
        ) : undefined} />
      {body}
      {canManage ? (
        <AdvisorDialog open={advisor} slug={slug} clears={1} onClose={() => { setAdvisor(false); focusLater(advisorOpener.current); }}
          onDone={(m) => { setAdvisor(false); onDone(m, 'item-h1'); }} />
      ) : null}
    </div>
  );
}

function HatBlock({ title, children }: { title: string; children: React.ReactNode }) {
  const id = `hat-${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  return <section className="gp-hat-block" aria-labelledby={id}><h2 id={id}>{title}</h2>{children}</section>;
}

function NotOpen({ prefix }: { prefix: string }) {
  return <EmptyState title="Nothing for you to do here" description="It isn’t open for you, or it was already handled." action={<Link className="gp-linkbtn" to={`${prefix}/today`}>Back to Today</Link>} />;
}

function SignatureBlocks({ slug, prefix, me, row, names, todayDay, batchIds, isLead, request, pkg, onDone, onAdvisor }: {
  slug: string; prefix: string; me: string; row: OwedRow | undefined; names: Names; todayDay: string; batchIds: string[]; isLead: boolean;
  request: SignatureView; pkg: PackageCard | null; onDone: (m: string, f: string) => void; onAdvisor?: (el: HTMLElement) => void;
}) {
  const blocks: React.ReactNode[] = [];
  const signing = row && row.action === 'sign';
  if (signing) {
    const forWho = row.on_behalf_of_user_id ? `For ${firstName(nameOf(names, row.on_behalf_of_user_id))} · delegated` : 'As approver';
    blocks.push(<HatBlock key="approver" title={forWho}><SignatureAction slug={slug} request={request} row={row} names={names} todayDay={todayDay} readTo="#locked" onDone={(m) => onDone(m, 'item-h1')} /></HatBlock>);
  }
  // V98-5-4: the approver's card shows the package's prerequisites — the same list as the chips and the calendar.
  if (pkg && pkg.prerequisites.length) blocks.push(<PrereqSummary key="prereqs" pkg={pkg} names={names} />);
  const preparer = request.preparer_user_id === me;
  const requester = request.requested_by_user_id === me;
  // "As requester" is not "As author" (U14): the requester asked for the signature; the author wrote the item.
  const yourPart = preparer ? 'As author' : 'As requester';
  if ((preparer || requester) && request.state === 'requested') {
    const routed = request.routing_status === 'routed' && !!request.approver_user_id && request.approver_user_id !== me;
    blocks.push(
      <HatBlock key="preparer" title={yourPart}>
        <SodPanel slug={slug} kind={'signature_request' as CoverageItemKind} id={request.request_id} routed={routed} routedTo={request.approver_user_id}
          names={names} batchIds={batchIds.length ? batchIds : undefined} onDone={onDone} onAddAdvisor={onAdvisor} />
      </HatBlock>,
    );
  }
  if ((preparer || requester) && request.state === 'returned') {
    blocks.push(<HatBlock key="revise" title={yourPart}><ReviseBlock slug={slug} prefix={prefix} requestId={request.request_id} names={names} onDone={onDone} /></HatBlock>);
  }
  // T5-H10: an obsolete / withdrawn version says why, points at the newest version (or who freezes next), and tucks its content away.
  const gone = request.state === 'obsolete' || request.state === 'withdrawn';
  const newer = gone && pkg?.latest && pkg.latest.version > request.version && pkg.latest.state !== 'obsolete' && pkg.latest.state !== 'withdrawn' ? pkg.latest : null;
  const status = gone ? (
    <HatBlock key="state" title="Status">
      <div className="gp-state" id="sod-state" tabIndex={-1} data-part="sig-obsolete">
        <strong>{`v${request.version} is ${request.state === 'obsolete' ? 'obsolete' : 'withdrawn'}${request.reason ? ` · ${request.reason}` : ''}`}</strong>
        {newer
          ? <span><Link to={`${prefix}/items/signature_request/${encodeURIComponent(newer.request_id)}?from=today`}>{`Open v${newer.version}`}</Link></span>
          : <span className="gp-dim">{pkg?.freezer_user_id ? `${nameOf(names, pkg.freezer_user_id)} freezes the next version` : 'The program lead freezes the next version'}</span>}
      </div>
    </HatBlock>
  ) : (
    <HatBlock key="state" title="Status">
      <div className="gp-state" id="sod-state" tabIndex={-1}>
        <strong>{request.state === 'signed' ? `Signed by ${nameOf(names, request.signed_by_user_id)}` : request.state === 'returned' ? 'Sent back to the author'
          : request.approver_user_id ? `With ${nameOf(names, request.approver_user_id)}` : 'Waiting for an approver'}</strong>
        <span className="gp-dim">{`v${request.version} · ${request.title}`}</span>
      </div>
    </HatBlock>
  );
  // R3-S3 / R3-S4: a lead (owner/admin or the program lead — the server decides) gets the resolve actions
  // first: the band's "Resolve" on a projected miss lands on them. They replace the plain status (same facts);
  // a 403 falls back to it.
  const lead = isLead && request.state === 'requested';
  if (lead) blocks.unshift(<LeadActions key="lead" slug={slug} requestId={request.request_id} names={names} todayDay={todayDay} onDone={onDone} fallback={blocks.length ? null : status} />);
  else if (!blocks.length) blocks.push(status);
  if (gone) {
    if (!blocks.includes(status)) blocks.unshift(status);
    blocks.push(<details key="locked" className="gp-how gp-old-version"><summary>{`Show v${request.version} as it was`}</summary><LockedContent request={request} /></details>);
  } else if (request.state !== 'returned') blocks.push(<LockedContent key="locked" request={request} />);
  return <>{blocks}</>;
}

type PackageCard = {
  prerequisites: Array<{ prerequisite_id: string; title: string; state: string; blocks_signing: boolean; cleared_at: string | null; cleared_by: string | null }>;
  blocking: number; latest?: { request_id: string; version: number; state: string } | null; freezer_user_id?: string | null;
};

function PrereqSummary({ pkg, names }: { pkg: PackageCard; names: Names }) {
  const done = pkg.prerequisites.filter((p) => p.state !== 'open').length;
  return (
    <HatBlock title={`Prerequisites · ${done} of ${pkg.prerequisites.length} done`}>
      <ul className="gp-owed-list gp-owed-list--flat" aria-label="Prerequisites" data-part="approver-prereqs">
        {pkg.prerequisites.map((p) => (
          <li key={p.prerequisite_id} className="gp-ow"><span className="gp-ow-main">
            <span className="gp-ow-t">{p.title}</span>
            <span className="gp-ow-meta">
              <Chip text={p.state === 'open' ? (p.blocks_signing ? 'Blocks signing' : 'Won’t block') : p.state === 'withdrawn' ? 'No longer applies' : 'Done'} tone={p.state === 'open' ? (p.blocks_signing ? 'red' : 'neutral') : 'ok'} />
              {p.state !== 'open' && p.cleared_by && !p.cleared_by.startsWith('system:') ? <span>{nameOf(names, p.cleared_by)}</span> : null}
            </span>
          </span></li>
        ))}
      </ul>
    </HatBlock>
  );
}

function AskBlock({ slug, prefix, askId, names, me, todayDay, onDone }: {
  slug: string; prefix: string; askId: string; names: Names; me: string; todayDay: string; onDone: (m: string, f: string) => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['coverage', slug, 'asks'], queryFn: () => listMyAsks(slug), retry: false });
  const ask = q.data?.asks.find((a) => a.ask_id === askId);
  const preview = useQuery({ queryKey: ['coverage', slug, 'preview', ask?.absence_id ?? ''], queryFn: () => absencePreview(slug, ask!.absence_id!), enabled: ask?.kind === 'confirm' && !!ask.absence_id, retry: false });
  const remoteSig = useQuery({ queryKey: ['signature', slug, ask?.item_id ?? ''], queryFn: () => getSignature(slug, ask!.item_id!), enabled: ask?.kind === 'sign_remote' && !!ask.item_id, retry: false });
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [who, setWho] = useState('');
  const [until, setUntil] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declineErr, setDeclineErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // R3-U10: signing remotely has the same 6-digit code fallback as every other signature.
  const [useCode, setUseCode] = useState(false);
  const [code, setCode] = useState('');
  const candidates = useMemo(() => [...names.keys()].filter((u) => u !== me), [names, me]);
  if (q.isLoading) return <p className="gp-dim">Loading the ask…</p>;
  if (!ask) return <EmptyState title="This ask is no longer open" description="It was answered or withdrawn." action={<Link className="gp-linkbtn" to={`${prefix}/today`}>Back to Today</Link>} />;
  const finish = async (msg: string) => { setDone(msg); await qc.invalidateQueries({ queryKey: ['today', slug] }); onDone(msg, 'ask-done'); };
  const answer = async (body: Parameters<typeof answerAsk>[2], msg: string) => {
    setBusy(true); setError(null);
    try { await answerAsk(slug, askId, body); await finish(msg); return true; }
    catch (e) { setError(errorText(e, 'The answer could not be recorded.')); return false; }
    finally { setBusy(false); }
  };
  const clear = (k: string) => { setError(null); if (errors[k]) setErrors((x) => ({ ...x, [k]: '' })); };
  if (done) return <HatBlock title="Asked of you"><div className="gp-state gp-state--ok" id="ask-done" tabIndex={-1}><strong>{done}</strong></div></HatBlock>;
  const delegate = async () => {
    const e: Record<string, string> = {};
    if (!who) e.who = 'Choose who covers it.';
    if (!until) e.until = 'Choose the last day.';
    else if (until < todayDay) e.until = 'Today or later.';
    setErrors(e);
    if (Object.values(e).some(Boolean)) return;
    await answer({ answer: 'delegate', delegation: { delegate_user_id: who, ends_at: `${until}T23:59:59.000Z` } }, `Delegated to ${nameOf(names, who)}`);
  };
  const signNow = async (r: SignatureView) => {
    if (useCode && !/^\d{6}$/.test(code)) { setErrors({ code: 'Enter the 6 digits from your authenticator app.' }); return; }
    setBusy(true); setError(null);
    try {
      await signRemote(slug, askId, { version: r.version, content_sha256: r.content_sha256, method: useCode ? 'code' : 'passkey', ...(useCode ? { assertion: code } : {}) });
      await finish(`Signed ${r.title} v${r.version}`);
    }
    catch (e) { setError(errorText(e, 'The signature could not be recorded.')); }
    finally { setBusy(false); }
  };
  const asks = ask.kind === 'confirm' ? 'confirm an absence' : ask.kind === 'sign_remote' ? 'sign while you’re away' : 'delegate this item';
  return (
    <HatBlock title="Asked of you">
      <div className="gp-state"><strong>{`${nameOf(names, ask.from_user_id)} asks you to ${asks}`}</strong>{ask.message ? <span>{ask.message}</span> : null}</div>
      {/* AskBlock confirm shows what moves BEFORE Confirm (U14). */}
      {ask.kind === 'confirm' ? (preview.data ? <AbsencePreviewList p={preview.data.preview} names={names} prefix={prefix} /> : preview.isLoading ? <p className="gp-dim">Loading where their items go…</p> : null) : null}
      {ask.kind === 'sign_remote' && remoteSig.data ? (
        <div className="gp-state">
          <strong>{`${remoteSig.data.request.title} · v${remoteSig.data.request.version}`}</strong>
          <span><Link to={`${prefix}/items/signature_request/${encodeURIComponent(remoteSig.data.request.request_id)}#locked`}>{`Read v${remoteSig.data.request.version}`}</Link></span>
        </div>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {ask.kind === 'sign_remote' && remoteSig.data && useCode ? (
        <Field id="remote-code" label="6-digit code" hint="From your authenticator app" error={errors.code}>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => { setCode(e.currentTarget.value.replace(/\D/g, '')); clear('code'); }} />
        </Field>
      ) : null}
      {ask.kind === 'delegate' ? (
        <div className="gp-form-row">
          <Field id="ask-who" label="Delegate to" required error={errors.who}>
            <Select value={who} onChange={(e) => { setWho(e.currentTarget.value); clear('who'); }}>
              <option value="">Choose…</option>
              {candidates.map((u) => <option key={u} value={u}>{nameOf(names, u)}</option>)}
            </Select>
          </Field>
          <Field id="ask-until" label="Until" required error={errors.until}>
            <Input type="date" min={todayDay} value={until} onChange={(e) => { setUntil(e.currentTarget.value); clear('until'); }} />
          </Field>
        </div>
      ) : null}
      <div className="gp-actions">
        {ask.kind === 'confirm'
          ? <Button variant="primary" aria-disabled={busy || undefined} onClick={() => void answer({ answer: 'confirm' }, 'Absence confirmed · their items route now')}>Confirm</Button>
          : ask.kind === 'sign_remote'
            ? (remoteSig.data ? <>
                <Button variant="ghost" aria-label={`${useCode ? 'Use passkey' : 'Use a code instead'} · ${remoteSig.data.request.title}`}
                  onClick={() => { setUseCode((u) => !u); clear('code'); }}>{useCode ? 'Use passkey' : 'Use a code instead'}</Button>
                <Button variant="primary" aria-disabled={busy || undefined} onClick={() => void signNow(remoteSig.data!.request)}>{`Sign v${remoteSig.data.request.version} with ${useCode ? 'code' : 'passkey'}`}</Button>
              </> : null)
            : <Button variant="primary" aria-disabled={busy || undefined} onClick={() => void delegate()}>Delegate</Button>}
        <Button id="ask-decline" onClick={() => setDeclining(true)}>{ask.kind === 'sign_remote' ? 'I can’t…' : 'Decline…'}</Button>
      </div>
      <ActionDialog open={declining} title={ask.kind === 'sign_remote' ? 'You can’t sign it' : 'Decline the ask'} submitLabel="Send" busy={busy} error={declineErr}
        onClose={() => { setDeclining(false); setDeclineErr(null); }}
        onSubmit={async () => {
          if (!reason.trim()) { setErrors({ reason: 'Give a reason (required).' }); return false; }
          setBusy(true); setDeclineErr(null);
          try {
            await answerAsk(slug, askId, { answer: 'decline', reason: reason.trim() });
            setDeclining(false);
            await finish('Declined · the asker sees your reason');
            return true;
          } catch (e) { setDeclineErr(errorText(e, 'The answer could not be recorded.')); return false; }
          finally { setBusy(false); }
        }}>
        <Field id="ask-reason" label="Reason" required hint="Required · up to 2000 characters · the asker sees it" error={errors.reason}>
          <Textarea rows={3} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setDeclineErr(null); if (errors.reason) setErrors((x) => ({ ...x, reason: '' })); }} />
        </Field>
      </ActionDialog>
    </HatBlock>
  );
}
